import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthTokenResponse } from './auth.model';
import { LUMEN_ACCESS_TOKEN_KEY } from './auth.constants';


/**
 * Service for handling authentication-related operations such as login, logout, and token
 * management. It maintains the access token in an Angular signal and provides methods to interact
 * with the authentication API endpoints. The service also includes logic to attempt session
 * restoration on app initialisation and to clear the session on logout or token refresh failure.
 *
 * @author sadatmalik
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly authBaseUrl = `${environment.apiBaseUrl}/api/auth`;

  private readonly accessToken = signal<string | null>(null);

  readonly isAuthenticated = computed(() => !!this.accessToken());

  constructor() {
    // One-time migration cleanup from the previous sessionStorage-based access token model.
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(LUMEN_ACCESS_TOKEN_KEY);
    }
  }

  login(email: string, password?: string): Observable<AuthTokenResponse> {
    const trimmedPassword = password?.trim();
    const payload = trimmedPassword
      ? { email: email.trim(), password: trimmedPassword }
      : { email: email.trim() };
    return this.http.post<AuthTokenResponse>(`${this.authBaseUrl}/login`, payload, {
      withCredentials: true
    }).pipe(
      tap((res) => {
        this.accessToken.set(res.accessToken);
      })
    );
  }

  refresh(): Observable<AuthTokenResponse> {
    return this.http.post<AuthTokenResponse>(`${this.authBaseUrl}/refresh`, {}, {
      withCredentials: true
    }).pipe(
      tap((res) => this.accessToken.set(res.accessToken))
    );
  }

  tryRestoreSession(): Observable<boolean> {
    return this.refresh().pipe(
      map(() => true),
      catchError(() => {
        this.clearSession();
        return of(false);
      })
    );
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.authBaseUrl}/logout`, {}, {
      withCredentials: true
    }).pipe(
      catchError(() => of(void 0)),
      tap(() => this.clearSession())
    );
  }

  clearSession(): void {
    this.accessToken.set(null);
  }

  setAccessToken(accessToken: string | null): void {
    this.accessToken.set(accessToken);
  }

  getAccessToken(): string | null {
    return this.accessToken();
  }
}
